import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProfileRewardAttributes } from '@loyalty/shared-common/profile-api-client';
import { RewardType } from '@loyalty/shared-common/rewards-api-client';
import { colorPalette, sxs, useAlerts } from '@qualifio/ui-commons';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faGift } from '@fortawesome/pro-solid-svg-icons/faGift';
import { faTicketAlt } from '@fortawesome/pro-solid-svg-icons/faTicketAlt';
import { faUsdCircle } from '@fortawesome/pro-solid-svg-icons/faUsdCircle';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useTranslation } from 'react-i18next';
import { CurrencyImage } from '../../../shared/currency/CurrencyImage';
import { Loader } from '../../../shared/layout/Loader';
import { QlfDate } from '../../../shared/qlf-date/QlfDate';
import { SxStyles } from '../../../shared/utils/types';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../../../currency/CurrenciesContext';
import { useProfiles } from '../../ProfileContext';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useRewards } from '../../../rewards/RewardsContext';

const pageSize = 10;

const styles: SxStyles = {
  headerCell: {
    fontSize: '14px',
    padding: 1,
    textAlign: 'left',
    '&:first-of-type': {
      borderTopLeftRadius: 5,
    },
    '&:last-of-type': {
      borderTopRightRadius: 5,
    },
  },
  headerIcon: {
    marginRight: 0.5,
  },
  cell: {
    padding: 1,
    borderTop: '1px solid #EAEAEA',
    borderBottom: '1px solid #EAEAEA',
  },
};

export function ProfileRewards() {
  const { t, i18n } = useTranslation();
  const { selectedProfile } = useProfiles();
  const { programId } = useProgram();
  const { currenciesById } = useCurrencies();
  const { profileApi } = useApi();
  const { notify } = useAlerts();
  const [currentPage, setCurrentPage] = useState(0);
  const [rewardsData, setRewardsData] = useState({
    data: [] as ProfileRewardAttributes[],
    isLast: true,
    pageCount: 0,
  });
  if (!selectedProfile) {
    throw new Error('No profile selected');
  }

  const fetchProfileRewards = async (limit: number, skip: number) => {
    const rewardsInfo = await profileApi.getProfileRewards(selectedProfile.id, {
      programId,
      limit,
      skip,
      orderBy: '-createdAt',
    });
    setRewardsData(rewardsInfo);
  };

  useEffect(() => {
    fetchProfileRewards(pageSize, 0);
  }, [selectedProfile]);

  const nextPage = async () => {
    await fetchProfileRewards(pageSize, (currentPage + 1) * pageSize);
    setCurrentPage(prev => prev + 1);
  };

  const previousPage = async () => {
    await fetchProfileRewards(pageSize, (currentPage - 1) * pageSize);
    setCurrentPage(prev => prev - 1);
  };

  const copy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text).catch(console.error);
      notify(t('profiles.details.rewards.promocode.copied'), 'success');
    },
    [notify],
  );

  const { rewards, fetchRewards, isLoading } = useRewards();

  useEffect(() => {
    if (!rewards) {
      fetchRewards();
    }
  }, []);

  const loadingRewards = isLoading('fetchRewards');

  if (rewardsData?.data?.length === 0) {
    return (
      <Box component="p" sx={{ color: colorPalette.grey.mainHover }}>
        {t('profiles.details.rewards.no-reward')}
      </Box>
    );
  }

  return (
    <div>
      <Box component="table" sx={{ width: '100%', borderSpacing: 0 }}>
        <Box
          component="thead"
          sx={{
            backgroundColor: colorPalette.marine.main,
            color: colorPalette.marine.contrastText,
            fontWeight: 'bold',
            fontSize: '1.15em',
          }}
        >
          <tr>
            <Box component="th" sx={styles.headerCell}>
              <Box
                component={FontAwesomeIcon}
                icon={faCalendar}
                sx={styles.headerIcon}
                fixedWidth
              />
              {t('profiles.details.rewards.list.date')}
            </Box>
            <Box component="th" sx={styles.headerCell}>
              <Box component={FontAwesomeIcon} icon={faGift} sx={styles.headerIcon} fixedWidth />
              {t('profiles.details.rewards.list.name')}
            </Box>
            <Box component="th" sx={styles.headerCell}>
              <Box
                component={FontAwesomeIcon}
                icon={faTicketAlt}
                sx={styles.headerIcon}
                fixedWidth
              />
              {t('profiles.details.rewards.list.promocode')}
            </Box>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'right' })}>
              <FontAwesomeIcon icon={faUsdCircle} fixedWidth />
            </Box>
          </tr>
        </Box>
        <tbody>
          {rewardsData.data.map(profileReward => {
            const currency = profileReward.currencyId
              ? currenciesById[profileReward.currencyId]
              : undefined;
            const amount = profileReward.amount;
            const reward = rewards.find(r => r.id === profileReward.rewardId);

            return (
              <Box
                component="tr"
                key={profileReward.id}
                sx={{ background: ({ palette }) => palette.background.paper }}
              >
                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    color: colorPalette.grey.dark,
                  })}
                >
                  {profileReward.createdAt ? (
                    <QlfDate date={profileReward.createdAt} />
                  ) : (
                    <> {'No date'}</>
                  )}
                </Box>
                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    color: colorPalette.grey.dark,
                  })}
                >
                  {getLocalizedProperty(profileReward, 'name', i18n.language)}
                </Box>
                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    fontFamily: 'Bebas Neue, Open Sans, sans-serif',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: colorPalette.orange.main,
                    minWidth: 'fit-content',
                  })}
                >
                  {loadingRewards ? (
                    <Loader />
                  ) : (
                    reward?.type === RewardType.PROMOCODE && <>{profileReward.promocode}</>
                  )}
                  <Box
                    component={FontAwesomeIcon}
                    icon={faCopy}
                    size="xs"
                    sx={{ marginLeft: 0.5, cursor: 'pointer' }}
                    onClick={() => copy(profileReward.promocode || '')}
                  />
                </Box>
                <Box
                  component="td"
                  sx={sxs(styles.cell, {
                    fontFamily: 'Bebas Neue, Open Sans, sans-serif',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textAlign: 'right',
                    color: colorPalette.marine.main,
                    minWidth: 'fit-content',
                    width: 100,
                  })}
                >
                  {amount || t('profiles.details.rewards.list.free')}
                  {amount && currency ? (
                    <CurrencyImage
                      currency={currency}
                      sx={{
                        display: 'inline-block',
                        verticalAlign: 'bottom',
                        marginLeft: 0.25,
                      }}
                    />
                  ) : (
                    ''
                  )}
                </Box>
              </Box>
            );
          })}
        </tbody>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          alignItems: 'center',
        }}
      >
        <Button color="grey" disabled={currentPage === 0} onClick={previousPage}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Box sx={{ fontSize: 16, marginX: 1 }}>{`${currentPage + 1} / ${
          rewardsData.pageCount == 0 ? 1 : Math.ceil(rewardsData.pageCount)
        }`}</Box>
        <Button color="grey" disabled={rewardsData.isLast} onClick={nextPage}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </Box>
    </div>
  );
}
